/**
 * Normalize project data at the boundary.
 *
 * Ensures the data contract is met before it reaches any downstream code:
 * - `pages` and `custom_pages` contain only non-empty strings
 * - `page_names` values are all strings (no null/undefined)
 * - `business_name` and `industry` are non-empty strings
 * - Functionality arrays contain only non-empty strings
 * - Phase 2 pages array is clean
 *
 * This runs:
 * 1. In the build-worker right after loading from DB (primary defense)
 * 2. In the frontend before every save (prevent bad data from entering DB)
 * 3. During trial-to-project conversion
 *
 * Mutates in place for performance (project data can be large).
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeProjectData(data: any): void {
  if (!data) return

  if (data.phase1) {
    normalizePhase1(data.phase1)
  }

  if (data.phase2) {
    normalizePhase2(data.phase2)
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizePhase1(p1: any): void {
  // --- Page arrays: filter out null/undefined/empty-string entries ---
  if (Array.isArray(p1.pages)) {
    p1.pages = p1.pages.filter((p: unknown) => typeof p === 'string' && p.length > 0)
  } else {
    p1.pages = []
  }

  if (Array.isArray(p1.custom_pages)) {
    p1.custom_pages = p1.custom_pages.filter((p: unknown) => typeof p === 'string' && p.length > 0)
  } else {
    p1.custom_pages = []
  }

  // --- page_names: ensure every value is a string, remove orphaned keys ---
  if (p1.page_names && typeof p1.page_names === 'object') {
    const validPageIds = new Set([...p1.pages, ...p1.custom_pages])
    const cleaned: Record<string, string> = {}

    for (const [key, val] of Object.entries(p1.page_names)) {
      if (!validPageIds.has(key)) continue // Remove orphaned entries
      cleaned[key] = (val != null && typeof val === 'string' && val.length > 0)
        ? val
        : key // Fall back to the page ID itself
    }

    p1.page_names = cleaned
  } else {
    p1.page_names = {}
  }

  // --- page_content: ensure it's an object ---
  if (!p1.page_content || typeof p1.page_content !== 'object') {
    p1.page_content = {}
  }

  // --- Critical string fields ---
  if (!p1.business_name || typeof p1.business_name !== 'string') {
    p1.business_name = 'My Website'
  }
  if (!p1.industry || typeof p1.industry !== 'string') {
    p1.industry = 'general'
  }

  // --- Functionality sub-objects ---
  if (p1.functionality) {
    const func = p1.functionality

    // Languages
    if (func.languages) {
      if (Array.isArray(func.languages.additional_languages)) {
        func.languages.additional_languages = func.languages.additional_languages
          .filter((l: unknown) => typeof l === 'string' && l.length > 0)
      }
    }

    // Integration arrays
    if (func.integrations) {
      for (const key of ['analytics', 'marketing', 'social', 'payment', 'other'] as const) {
        if (Array.isArray(func.integrations[key])) {
          func.integrations[key] = func.integrations[key]
            .filter((v: unknown) => typeof v === 'string' && v.length > 0)
        }
      }
    }

    // Additional features
    if (Array.isArray(func.additional)) {
      func.additional = func.additional
        .filter((v: unknown) => typeof v === 'string' && v.length > 0)
    }

    // Auth user_roles
    if (func.authentication && Array.isArray(func.authentication.user_roles)) {
      func.authentication.user_roles = func.authentication.user_roles
        .filter((v: unknown) => typeof v === 'string' && v.length > 0)
    }
  }

  // --- Style keywords ---
  if (Array.isArray(p1.style_keywords)) {
    p1.style_keywords = p1.style_keywords
      .filter((v: unknown) => typeof v === 'string' && v.length > 0)
  }

  // --- References ---
  if (Array.isArray(p1.references)) {
    p1.references = p1.references
      .filter((r: unknown) => r != null && typeof r === 'object' && typeof (r as any).url === 'string')
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizePhase2(p2: any): void {
  if (!Array.isArray(p2.pages)) {
    p2.pages = []
    return
  }

  // Filter out null entries and ensure each page has required fields
  p2.pages = p2.pages.filter((page: unknown) => {
    if (!page || typeof page !== 'object') return false
    const p = page as any
    return typeof p.name === 'string' && p.name.length > 0
  })

  // Clean sections within each page
  for (const page of p2.pages) {
    if (Array.isArray(page.sections)) {
      page.sections = page.sections.filter(
        (s: unknown) => s != null && typeof s === 'object' && typeof (s as any).type === 'string'
      )
    }
  }
}
